<script setup lang="ts">
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useQuasar } from 'quasar'
import { formatDate, formatDateTime } from '../../utils/format'
import { useRestData } from '../../composables/useRestData'
import { useApi } from '../../composables/api'
import { moderationListFilters } from '../../utils/moderation-list-route'
import CrudDataTable from '../../components/CrudDataTable.vue'
import DateTimePicker from '../../components/forms/DateTimePicker.vue'
import LocationPicker from '../../components/forms/LocationPicker.vue'
import IncidentMediaSection from '../../components/staff/IncidentMediaSection.vue'
import { formatIncidentType } from '../../utils/format'
import {
  CrashLinkStatus,
  IncidentReportType,
  ModerationStatus,
  type IncidentReport
} from '@roadfootage/api-client'

const { adminIncidentReportsApi } = useApi()
const $q = useQuasar()
const bulkUpdating = ref(false)
const modalTab = ref<'details' | 'media'>('details')

const bulkSetStatus = (selected: any[], clearSelection: () => void, status: ModerationStatus) => {
  const label = status === ModerationStatus.Approved ? 'Approve' : 'Reject'
  $q.dialog({
    title: `Bulk ${label}`,
    message: `${label} ${selected.length} incident report${selected.length !== 1 ? 's' : ''}?`,
    cancel: true,
    ok: { label, color: status === ModerationStatus.Approved ? 'positive' : 'negative' }
  }).onOk(async () => {
    bulkUpdating.value = true
    const results = await Promise.allSettled(
      selected.map((item) =>
        adminIncidentReportsApi.adminIncidentReportsUpdateById({
          id: item.id,
          operation: [{ op: 'replace', path: '/moderationStatus', value: status }]
        })
      )
    )
    const failed = results.filter((r) => r.status === 'rejected').length
    bulkUpdating.value = false
    clearSelection()
    reportData.refresh()
    if (failed === 0) {
      $q.notify({
        type: 'positive',
        message: `${selected.length} report${selected.length !== 1 ? 's' : ''} ${label.toLowerCase()}d.`
      })
    } else {
      $q.notify({
        type: 'warning',
        message: `${selected.length - failed} ${label.toLowerCase()}d, ${failed} failed.`
      })
    }
  })
}
const router = useRouter()
const route = useRoute()

// 1. Setup the headless data fetcher
const reportData = useRestData(
  (params) => adminIncidentReportsApi.adminIncidentReportsPaginate(params),
  {
    initialPageSize: 25,
    defaultSort: [{ sortBy: 'createdAt', descending: true }]
  }
)

Object.assign(reportData.activeFilters.value, moderationListFilters(route.query, new Date()))

// Initial fetch
reportData.fetch()

// 2. Define the columns based on the IncidentReport Swagger schema
const columns = [
  {
    name: 'incidentReportType',
    field: 'incidentReportType',
    label: 'Type',
    align: 'left',
    sortable: true,
    format: (val: string) => formatIncidentType(val)
  },
  {
    name: 'moderationStatus',
    field: 'moderationStatus',
    label: 'Status',
    align: 'left',
    sortable: true
  },
  {
    name: 'crashLinkStatus',
    field: (r: IncidentReport) => (r.openDataCrashId ? (r.crashLinkStatus ?? 'Pending') : ''),
    label: 'Crash link',
    align: 'left',
    sortable: true
  },
  {
    name: 'incidentDateTime',
    field: 'incidentDateTime',
    label: 'Incident Date',
    align: 'left',
    sortable: true,
    format: (val: string) => (val ? formatDateTime(val) : 'N/A')
  },
  {
    name: 'createdAt',
    field: 'createdAt',
    label: 'Submitted',
    align: 'left',
    sortable: true,
    format: (val: string) => (val ? formatDate(val) : '')
  },
  {
    name: 'formattedAddress',
    field: (r: IncidentReport) => r.address?.formattedAddress,
    label: 'Address',
    align: 'left',
    sortable: true,
    format: (val: string) => (val ? val.substring(0, 50) + '...' : '')
  },
  {
    name: 'userSubmittedIncidentDescription',
    field: 'userSubmittedIncidentDescription',
    label: 'Creator Description',
    align: 'left',
    sortable: true,
    format: (val: string) => (val ? val.substring(0, 50) + '...' : '')
  },
  { name: 'creator', label: 'Creator', align: 'right', sortable: false },
  { name: 'actions', field: 'actions', label: 'Actions', align: 'right' }
]
</script>

<template>
  <q-page>
    <CrudDataTable
      title="Incident Report"
      :columns="columns"
      :rest-data="reportData"
      :update-api="(params) => adminIncidentReportsApi.adminIncidentReportsUpdateById(params)"
      :delete-api="(params) => adminIncidentReportsApi.adminIncidentReportsDeleteById(params)"
      enable-filters
      enable-multi-sort
      enable-search
      enable-selection
    >
      <template #bulk-actions="{ selected, clearSelection }">
        <q-btn
          flat
          dense
          icon="mdi-check-circle"
          label="Approve"
          color="positive"
          :loading="bulkUpdating"
          @click="bulkSetStatus(selected, clearSelection, ModerationStatus.Approved)"
        />
        <q-btn
          flat
          dense
          icon="mdi-close-circle"
          label="Reject"
          color="negative"
          :loading="bulkUpdating"
          @click="bulkSetStatus(selected, clearSelection, ModerationStatus.Rejected)"
        />
      </template>
      <template #update-form="{ item }">
        <q-tabs v-model="modalTab" dense align="left" class="q-mb-md" active-color="primary">
          <q-tab name="details" label="Details" />
          <q-tab name="media" label="Media" data-testid="media-tab" />
        </q-tabs>
        <q-tab-panels v-model="modalTab" keep-alive class="bg-transparent">
          <q-tab-panel name="details" class="q-pa-none">
            <div class="row q-col-gutter-md">
              <div class="col-12 col-md-6">
                <q-select
                  v-model="item.incidentReportType"
                  :options="Object.values(IncidentReportType)"
                  label="Incident Type"
                  outlined
                />
              </div>
              <div class="col-12 col-md-6">
                <q-select
                  v-model="item.moderationStatus"
                  :options="Object.values(ModerationStatus)"
                  label="Moderation Status"
                  outlined
                />
              </div>
              <div v-if="item.openDataCrashId" class="col-12 col-md-6">
                <q-select
                  v-model="item.crashLinkStatus"
                  :options="Object.values(CrashLinkStatus)"
                  label="Crash link status"
                  hint="Changing the crash id resets this to Pending"
                  outlined
                />
              </div>
              <div class="col-12 col-md-6">
                <DateTimePicker
                  :model-value="item.incidentDateTime ?? new Date()"
                  label="Incident Date/Time"
                  outlined
                  @update:model-value="item.incidentDateTime = $event"
                />
              </div>
              <div class="col-12 col-md-6">
                <q-input v-model="item.incidentTimezone" label="Incident Timezone" outlined />
              </div>
              <div class="col-12 col-md-3">
                <q-input v-model="item.countryCode" label="Country Code" outlined />
              </div>
              <div class="col-12 col-md-3 flex items-center">
                <q-toggle v-model="item.hasContentWarning" label="Content Warning" />
              </div>
              <div class="col-12 col-md-3 flex items-center">
                <q-toggle v-model="item.isFilmedInPublicLocation" label="Filmed in Public" />
              </div>
              <div class="col-12 col-md-3 flex items-center">
                <q-toggle v-model="item.userConsentProvided" label="Consent Provided" />
              </div>
              <div class="col-12">
                <LocationPicker
                  :location="item.location"
                  :address="item.address"
                  @update:location="item.location = $event"
                  @update:address="item.address = $event"
                />
              </div>
              <div class="col-12">
                <q-input
                  v-model="item.userSubmittedIncidentDescription"
                  label="Creator Incident Description"
                  type="textarea"
                  outlined
                  readonly
                />
              </div>
              <div class="col-12">
                <q-input
                  v-model="item.incidentDescription"
                  label="Staff Incident Description"
                  type="textarea"
                  outlined
                />
              </div>
            </div>
          </q-tab-panel>
          <q-tab-panel name="media" class="q-pa-none">
            <IncidentMediaSection :report="item" @changed="reportData.refresh()" />
          </q-tab-panel>
        </q-tab-panels>
      </template>
      <template #body-cell-creator="scope">
        <q-td :props="scope">
          <q-btn
            label="Creator"
            color="secondary"
            size="sm"
            @click="router.push({ name: 'users', query: { id: scope.row.userProfileId } })"
          />
        </q-td>
      </template>
    </CrudDataTable>
  </q-page>
</template>
